import { data } from "react-router-dom";
import { Ticket } from ".";
import { SupervisorService, TicketService } from "@/services";
import { getTicketStatus, uploadFilesAndGetLinks } from "@/utils/helpers";
import CustomerService from "@/services/Customer.Service";

/**
 * =========================
 * CHATBOT FLOW (High-level)
 * =========================
 * 0) Identity Capture (Linear flow)
 *    firstName -> lastName -> email -> organisationName -> queryType
 *
 * 1) Branching from `queryType`
 *    A) Support  -> support
 *         A1) New Ticket Path:
 *             newTicket -> ticketSubject -> ticketDescription -> reStartChat
 *         A2) Old Ticket Path:
 *             oldTicket(action: list user tickets OR no tickets) -> ticketDetails(action: show status & ask to add more info?)
 *                -> Yes  -> addMoreInfoInTicket -> ticketDetailsAddition -> reStartChat
 *                -> No   -> workingOnTicket -> reStartChat
 *
 *    B) Sales   -> sales -> getBackASAP -> reStartChat
 *                 (salesThanks node exists but is not referenced; kept intact)
 *
 *    C) Query   -> query -> getBackASAP -> reStartChat
 *
 * 2) Conversation End / Restart
 *    reStartChat -> (Yes: back to queryType) | (No: thanks)
 *    thanks: terminal message
 *
 * 3) Misc
 *    agent: alternative entry for free-text start (kept intact)
 *    salesThanks: unused in current graph (kept intact, logic unchanged)
 */

export interface ChatContext {
  chatbotId?: string;
  input?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  organisationName?: string;
  queryType?: string;
  newTicket?: string;
  data?: Ticket;
}

export interface ActionExtra {
  attachments?: File[];
  data?: Ticket;
}

export type Option = {
  text: string;
  next: keyof typeof chatConversationData;
  action?: (ctx: ChatContext, opt: Option) => Promise<any>;
  data?: Ticket | undefined;
};

export interface TableCol {
  header: string;
  accessor: string;
  render?: (row: any) => React.ReactNode;
}

export interface TableData {
  next: keyof typeof chatConversationData,
  [key: string]: any
}

export interface Table {
  cols: TableCol[];
  data?: TableData[];
}

export type ActionResponse = {
  text?: string
  options?: Option[]
  next?: string
  table?: Table
}

export interface GoToNodePayload {
  key: keyof typeof chatConversationData;
  userValue: string;
  extraData?: ActionExtra | TableData;
  attachments?: File[];
  reset?: boolean;
}

export interface Message {
  sender: 'bot' | 'user';
  text: string;
  options?: Option[];
  attachments?: File[];
  table?: Table;
}

export type ConvoNode = {
  text?: string;
  input?: 'text' | 'email';
  options?: Option[];
  table?: TableCol[];
  next?: string;
  action?: (value: ChatContext, userValue: string, extra?: ActionExtra) => Promise<ActionResponse>;
  attachment?: boolean;
};

export const chatConversationData: Record<string, ConvoNode> = {
  /**
   * ======================================
   * (0) IDENTITY CAPTURE (Linear sequence)
   * ======================================
   */
  firstName: { text: "Hello, I'm your virtual assistant. May I have your first name, please?", input: 'text', next: 'lastName' },
  lastName: { text: "What is your last name?", input: 'text', next: 'email' },
  email: { text: "What is your email?", input: 'email', next: 'organisationName' },
  organisationName: {
    text: "What is your organisation name?",
    input: 'text',
    next: 'queryType',
    options: [{ text: 'Skip', next: 'queryType' }],
  },

  /**
   * ==========================
   * (1) BRANCHING ENTRY POINT
   * ==========================
   */
  queryType: {
    text: "What is your reason for contacting us today?",
    options: [
      { text: 'Support', next: 'support' },
      { text: 'Sales', next: 'sales' },
      { text: 'Query', next: 'query' },
    ]
  },

  /**
   * 1A) SUPPORT BRANCH
   */
  support: {
    text: "Old ticket or New ticket?",
    options: [
      { text: 'New Ticket', next: 'newTicket' },
      { text: 'Old Ticket', next: 'oldTicket' }
    ]
  },

  // --- A1) New Ticket Path ---
  newTicket: {
    text: "Please provide a subject for your ticket",
    input: 'text',
    next: 'ticketSubject'
  },
  ticketSubject: {
    text: "Please provide a description of your support request. Include an attachment if desired",
    input: 'text',
    next: 'ticketDescription',
    attachment: true,
  },
  ticketDescription: {
    // text: "Creating your ticket…",
    action: async (data, issueDetails, extra) => {
      const attachments = extra?.attachments || [];
      let publicLinks: string[] = [];
      if (attachments.length && data.chatbotId) {
        publicLinks = await uploadFilesAndGetLinks(attachments, data.chatbotId);
      }

      const resp = await SupervisorService.talkWithChatBot({
        ...data,
        queryType: data?.queryType?.toLowerCase(),
        subject: data.newTicket,
        issueDetails,
        ticketType: 'new',
        source: 'chatbot',
        attachments: publicLinks
      });
      const newTicket: Ticket = resp.data.ticket
      return { text: `Your ticket has been created successfully & your ticket ID is "${newTicket.ticketId}"` };
    },
    next: 'reStartChat'
  },

  // --- A2) Old Ticket Path ---
  oldTicket: {
    // text: "Fetching your open tickets…",
    action: async (data, _) => {
      try {
        const resp = await SupervisorService.talkWithChatBot({ ...data, ticketType: 'old', queryType: data?.queryType?.toLowerCase() });
        return {
          table: {
            cols: [
              { header: 'Ticket Id', accessor: 'ticketId' },
              { header: 'Subject', accessor: 'subject' }
            ],
            data: resp.data.map((d: Ticket) => ({ ...d, data: d, next: 'ticketDetails' })),
          },
          text: 'Please select one to proceed.',
        }
      } catch (error) {
        return { text: 'you have no tickets Or Your all tickets are closed', next: 'reStartChat' }
      }
    },
  },
  ticketDetails: {
    // text: "Checking ticket status…",
    action: async (data, _, extra) => {
      const chatbotId = data?.chatbotId
      const ticketId = extra?.data?.id
      if (ticketId && chatbotId) {
        const { data: t } = await TicketService.getDetailsForChat({ ticketId, chatbotId });
        return {
          text: `Your ticket status is "${getTicketStatus(t)}". Do you want to add more details?`,
          options: [
            { text: 'Yes', next: 'addMoreInfoInTicket' },
            { text: 'No', next: 'workingOnTicket' }
          ]
        };
      }
      return {};
    }
  },
  addMoreInfoInTicket: {
    text: "Please provide your details and related attachements (if any)",
    input: 'text',
    next: 'ticketDetailsAddition',
    attachment: true
  },
  ticketDetailsAddition: {
    // text: "Adding more details to your ticket…",
    input: 'text',
    action: async (data, _, extra) => {
      const ticketId = data?.data?.id;
      const attachments = extra?.attachments || [];
      let publicLinks: string[] = [];
      if (attachments.length && data.chatbotId && ticketId) {
        publicLinks = await uploadFilesAndGetLinks(attachments, data.chatbotId);
      }
      await SupervisorService.talkWithChatBot({
        ...data,
        queryType: data?.queryType?.toLowerCase(),
        ticketDescription: data.input,
        ticketType: 'old',
        source: 'chatbot',
        ticketId,
        isSatisfied: false,
        attachments: publicLinks,
      });
      return { text: `Details added to your ticket successfully` };
    },
    next: 'reStartChat'
  },
  workingOnTicket: {
    text: "Our team is working on your ticket. Thanks for your patience.",
    next: 'reStartChat',
  },

  /**
   * 1B) SALES BRANCH
   */
  sales: {
    text: "Please provide details what your are looking for",
    input: 'text',
    next: 'getBackASAP'
  },

  /**
   * 1C) GENERIC QUERY BRANCH
   */
  query: {
    text: "Please provide your query",
    input: 'text',
    next: 'getBackASAP'
  },

  /**
   * Common async handler for Sales/Query
   */
  getBackASAP: {
    text: "We will get back to you shortly.",
    action: async (data, _) => {
      return await SupervisorService.talkWithChatBot({ ...data, queryType: data?.queryType?.toLowerCase(), subject: data.input });
    },
    next: 'reStartChat'
  },

  /**
   * ============================
   * (2) END / RESTART DECISION
   * ============================
   */
  reStartChat: {
    text: "Do you need any further assistance?",
    options: [
      { text: 'Yes', next: 'queryType' },
      { text: 'No', next: 'thanks' }
    ]
  },
  thanks: {
    text: "Thank you for using the virtual assistant. Have a great day!",
  },

  /**
   * =================
   * (3) MISCELLANEOUS
   * =================
   * - `agent`: alternate free-text entry; kept intact.
   * - `salesThanks`: not referenced by current paths; kept intact.
   */
  agent: {
    text: "Hello, I'm your virtual assistant. How can I help you today?",
    options: [],
    next: 'reStartChat',
    input: 'text'
  },
  salesThanks: {
    text: "Thank you for using the virtual assistant. I have raised a request for you. You will be contacted shortly."
  }
};
